import { useTranslation } from 'react-i18next';
import { MessageSquareOff } from 'lucide-react';

import {
  PATIENT_INQUIRY_SORTABLE_KEYS,
  usePatientInquiryListStore,
} from '../store/patient-inquiry-list.store';
import type {
  PatientInquiryGender,
  PatientInquiryListItem,
  PatientInquirySortKey,
} from '../types/patient-inquiry.types';
import {
  formatInquiredAt,
  formatInquirer,
  formatInquirerUid,
  truncateTitle,
} from '../utils/format-patient-inquiry';
import { PatientInquiryStatusBadge } from './PatientInquiryStatusBadge';

interface PatientInquiryListTableProps {
  items: readonly PatientInquiryListItem[];
  onRowClick?: (id: number) => void;
}

interface ColumnDef {
  key: PatientInquirySortKey;
  i18nKey: string;
  width?: number;
}

// 7 columns per PRD §2.2 #4 / §4.1. Per ADM.035 only Inquiry Date & Time and
// Status are interactively sortable (see PATIENT_INQUIRY_SORTABLE_KEYS).
const COLUMNS: readonly ColumnDef[] = [
  { key: 'id', i18nKey: 'id', width: 60 },
  { key: 'uid', i18nKey: 'uid', width: 90 },
  { key: 'inquirerName', i18nKey: 'inquirer', width: 220 },
  { key: 'title', i18nKey: 'title' },
  { key: 'type', i18nKey: 'type', width: 160 },
  { key: 'inquiredAt', i18nKey: 'inquiredAt', width: 160 },
  { key: 'status', i18nKey: 'status', width: 120 },
];

// SHCT-805 — the width below which this table can only render by crushing 문의 제목: every pinned
// column at its declared width, plus a floor for the one unpinned column so the title still has room
// to read. Derived from COLUMNS rather than typed as a literal, so a column width change carries here.
const TITLE_COLUMN_MIN = 220;
const TABLE_MIN_WIDTH =
  COLUMNS.reduce((sum, col) => sum + (col.width ?? 0), 0) + TITLE_COLUMN_MIN;

// PRD §4.1 3-click cycle: at rest (userSorted=false) the default-sort column
// (inquiredAt) shows ↓; all other columns show ↕. Once a user picks a column,
// only that column shows ↑/↓ until the 3rd click clears it back to default.
function renderSortIcon(
  col: ColumnDef,
  sortKey: PatientInquirySortKey,
  sortDir: 'asc' | 'desc',
  userSorted: boolean,
): string {
  if (userSorted) {
    if (sortKey !== col.key) return '↕';
    return sortDir === 'asc' ? '↑' : '↓';
  }
  return col.key === 'inquiredAt' ? '↓' : '↕';
}

function ColumnHeaders() {
  const { t } = useTranslation('admin');
  const sort = usePatientInquiryListStore((s) => s.sort);
  const userSorted = usePatientInquiryListStore((s) => s.userSorted);
  const toggleSort = usePatientInquiryListStore((s) => s.toggleSort);

  return (
    <tr>
      {COLUMNS.map((col) => {
        // ADM.035: only inquiredAt / status get the sort control; the rest are
        // plain headers (no pointer, no click, no indicator).
        const sortable = PATIENT_INQUIRY_SORTABLE_KEYS.has(col.key);
        return (
          <th
            key={col.key}
            style={{ width: col.width, cursor: sortable ? 'pointer' : 'default' }}
            onClick={sortable ? () => toggleSort(col.key) : undefined}
          >
            {t(`patientInquiry.list.columns.${col.i18nKey}`)}
            {sortable && (
              <>
                {' '}
                <span className="sort-icon">
                  {renderSortIcon(col, sort.key, sort.dir, userSorted)}
                </span>
              </>
            )}
          </th>
        );
      })}
    </tr>
  );
}

// PRD §3.1: rows are clickable; clicking opens the 480px slide panel.
function PatientInquiryListRow({
  item,
  onClick,
}: {
  item: PatientInquiryListItem;
  onClick?: () => void;
}) {
  const { t } = useTranslation('admin');
  const genderLabels: Record<PatientInquiryGender, string> = {
    M: t('patientInquiry.genders.M'),
    F: t('patientInquiry.genders.F'),
  };
  const inquirer = formatInquirer(
    item,
    genderLabels,
    t('patientInquiry.withdrawn'),
    t('patientInquiry.error.patientInfo'),
  );

  return (
    <tr style={{ cursor: onClick ? 'pointer' : undefined }} onClick={onClick}>
      <td>{item.id}</td>
      <td>{formatInquirerUid(item.uid)}</td>
      <td style={item.patientLookupFailed ? { color: 'var(--destructive)' } : undefined}>
        {/* Text column — truncate to the column width, full value on hover. */}
        <span className="text-ellipsis" style={{ display: 'block' }} title={inquirer}>
          {inquirer}
        </span>
      </td>
      {/* SHCT-606 — truncate by pixel width too (not just by character count,
          truncateTitle), so a long unbroken title can't stretch the column. The
          tooltip shows the full title either way. */}
      <td className="cell-ellipsis" title={item.title}>
        <span className="link-text">{truncateTitle(item.title)}</span>
      </td>
      <td>
        <span className="badge badge-outline">
          {t(`patientInquiry.types.${item.type}`)}
        </span>
      </td>
      <td>{formatInquiredAt(item.inquiredAt)}</td>
      <td>
        <PatientInquiryStatusBadge status={item.status} />
      </td>
    </tr>
  );
}

export function PatientInquiryListTable({ items, onRowClick }: PatientInquiryListTableProps) {
  return (
    <div className="card">
      {/* SHCT-805 — the table is `table--fixed` with 810px of pinned column widths and ONE unpinned
          column (문의 제목) absorbing whatever is left. In a container that simply shrinks, "whatever is
          left" goes to zero: the title column collapsed into its neighbour and its text was clipped away
          entirely. A table cannot lay out narrower than its pinned columns without doing that, so the room
          it needs is guaranteed with a min-width and the container scrolls instead of squeezing — which is
          what the ticket asks for. Scoped to this table on purpose: other admin lists share the pattern,
          but changing them all is a separate, wider decision. */}
      <div style={{ overflowX: 'auto' }}>
        <table
          className="table table--clickable table--fixed"
          style={{ margin: 0, minWidth: TABLE_MIN_WIDTH }}
        >
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {items.map((item) => (
            <PatientInquiryListRow
              key={item.id}
              item={item}
              onClick={onRowClick ? () => onRowClick(item.id) : undefined}
            />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// PRD §5: loading skeleton = 5 rows × 7 cols.
export function PatientInquiryListSkeleton() {
  return (
    <div className="card">
      <div style={{ overflowX: 'auto' }}>
        <table
          className="table table--fixed"
          style={{ margin: 0, minWidth: TABLE_MIN_WIDTH }}
        >
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((col) => (
                <td key={col.key}>
                  <div
                    className="animate-pulse"
                    style={{ height: 14, background: 'var(--muted)', borderRadius: 4 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// PRD §3.1 / §3.2 / §5: empty copy differs when a search keyword is active.
export function PatientInquiryListEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <div style={{ overflowX: 'auto' }}>
        <table
          className="table table--fixed"
          style={{ margin: 0, minWidth: TABLE_MIN_WIDTH }}
        >
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--muted-foreground)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <MessageSquareOff size={48} color="var(--muted-foreground)" />
                <p style={{ margin: 0 }}>
                  {filtered
                    ? t('patientInquiry.empty.noResults')
                    : t('patientInquiry.empty.none')}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}
